import { useEffect, useState, useCallback, useRef } from "react";
import { getPaginatedExecutionSubGroup } from "../services/executionSubGroup";

interface IUseFetchExecutionSubGroup {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  page: any;
  pSize: any;
  searchTerm?: string;
  statusFilter?: string;
}

const useFetchPaginatedExecutionSubGroup = ({
  setIsLoading,
  page,
  pSize,
  searchTerm,
  statusFilter = "Active",
}: IUseFetchExecutionSubGroup) => {
  const [executionSubGroup, setExecutionSubGroup] = useState<any>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Map statusFilter to isActive for API call
  const isActive =
    statusFilter === "Active"
      ? "true"
      : statusFilter === "Inactive"
      ? "false"
      : "all";

  const fetchData = useCallback(
    async (searchQuery?: string) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const result = await getPaginatedExecutionSubGroup({
          page,
          pSize,
          searchTerm: searchQuery || searchTerm,
          isActive,
          signal: abortControllerRef.current.signal,
        });
        setExecutionSubGroup(result);
      } catch (err: any) {
        // Don't update state if request was aborted
        if (err.name !== "AbortError") {
          setExecutionSubGroup([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [page, pSize, searchTerm, isActive, setIsLoading]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [executionSubGroup, fetchData];
};

export default useFetchPaginatedExecutionSubGroup;
